import { useDeferredValue, useMemo, useState } from "react";
import { usePageBar } from "../contexts/PageBarContext";
import { Link, useSearchParams } from "react-router-dom";
import { useTripSearchCorpus } from "../hooks/useTripSearchCorpus";
import { searchTrips, snippetParts, type SearchHit, type SnippetPart } from "../utils/tripSearch";
import { dayKeyOf, formatDayKey } from "../utils/calendarDay";
import { formatDateLabel } from "../utils/tripDisplay";
import "./Search.css";

/** Hits shown per trip before "Show more". */
const PER_TRIP = 5;

function Highlight({ parts }: { parts: SnippetPart[] }) {
  return (
    <>
      {parts.map((p, i) => (p.hit ? <mark key={i}>{p.text}</mark> : <span key={i}>{p.text}</span>))}
    </>
  );
}

function tripDates(trip: { startDate: unknown; endDate: unknown }): string {
  const fmt = (v: unknown) => {
    const key = dayKeyOf(v as Parameters<typeof dayKeyOf>[0]);
    return key ? formatDayKey(key, { month: "short", day: "numeric", year: "numeric" }) : "";
  };
  const a = fmt(trip.startDate);
  const b = fmt(trip.endDate);
  return a && b && a !== b ? `${a} – ${b}` : a || b;
}

function HitRow({ hit }: { hit: SearchHit }) {
  const { doc, matches } = hit;
  // When the title is what matched, highlight it there rather than repeating
  // it as a snippet underneath.
  // A trip's own row sits under its name already, so it reads "Trip details"
  // and shows a match on the name like any other.
  const isTrip = doc.kind === "trip";
  const title = isTrip ? "Trip details" : doc.title;
  const titleMatch = isTrip ? undefined : matches.find((m) => m.kind === "name" && m.text === doc.title);
  const rest = matches.filter((m) => m !== titleMatch);
  const when =
    doc.kind === "trip" ? null : doc.when ? formatDateLabel(doc.when, doc.zone) : null;
  return (
    <li>
      <Link to={doc.href} className="search-hit">
        <span className="search-hit-icon" aria-hidden="true">{doc.icon}</span>
        <span className="search-hit-body">
          <span className="search-hit-title">
            {titleMatch ? <Highlight parts={snippetParts(title, titleMatch.ranges)} /> : title}
          </span>
          {(when || doc.context) && (
            <span className="search-hit-meta">
              {[when, doc.context].filter(Boolean).join(" · ")}
            </span>
          )}
          {rest.map((m, i) => (
            <span key={i} className="search-hit-match">
              <span className={`search-hit-label search-kind-${m.kind}`}>{m.label}</span>
              <span className="search-hit-snippet">
                <Highlight parts={snippetParts(m.text, m.ranges)} />
              </span>
            </span>
          ))}
        </span>
      </Link>
    </li>
  );
}

export function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  usePageBar({ title: "Search", showTitle: true });
  const [text, setText] = useState(() => searchParams.get("q") ?? "");
  const query = useDeferredValue(text);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const { docs, tripOrder, tripsById, loading, loadedTrips, totalTrips } = useTripSearchCorpus();

  const groups = useMemo(
    () => (query.trim().length >= 2 ? searchTrips(docs, query, tripOrder) : []),
    [docs, query, tripOrder],
  );
  const hitCount = groups.reduce((n, g) => n + g.hits.length, 0);

  const onChange = (value: string) => {
    setText(value);
    setExpanded(new Set());
    // Keep the query in the URL so going back from a result lands on the same list.
    setSearchParams(value ? { q: value } : {}, { replace: true });
  };

  const stillLoading = loading || loadedTrips < totalTrips;
  const searching = query.trim().length >= 2;

  return (
    <div className="page search-page">
      <h1 className="page-head">Search</h1>
      <form className="search-box" role="search" onSubmit={(e) => e.preventDefault()}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <line x1="16.5" y1="16.5" x2="21" y2="21" />
        </svg>
        <input
          type="search"
          className="search-input"
          placeholder="A name, a place, anything in your notes"
          aria-label="Search your trips"
          value={text}
          onChange={(e) => onChange(e.target.value)}
          autoFocus
          enterKeyHint="search"
        />
      </form>

      <p className="search-status text-muted" aria-live="polite">
        {searching
          ? `${hitCount === 0 ? "No matches" : `${hitCount} ${hitCount === 1 ? "match" : "matches"} in ${groups.length} ${groups.length === 1 ? "trip" : "trips"}`}${stillLoading ? ` · still checking ${totalTrips - loadedTrips} of ${totalTrips} trips` : ""}`
          : stillLoading
            ? `Loading ${totalTrips ? `${loadedTrips} of ${totalTrips} trips` : "trips"}…`
            : `Searches people, notes, places and orders across all ${totalTrips} trips.`}
      </p>

      {groups.map((g) => {
        const trip = tripsById.get(g.tripId);
        if (!trip) return null;
        const open = expanded.has(g.tripId);
        const shown = open ? g.hits : g.hits.slice(0, PER_TRIP);
        return (
          <section key={g.tripId} className="search-group">
            <header className="search-group-header">
              <Link to={`/trips/${trip.id}`} className="search-group-title">{trip.name}</Link>
              <span className="search-group-dates">{tripDates(trip)}</span>
            </header>
            <ul className="search-hits">
              {shown.map((h) => (
                <HitRow key={`${h.doc.kind}:${h.doc.id}`} hit={h} />
              ))}
            </ul>
            {g.hits.length > PER_TRIP && (
              <button
                type="button"
                className="search-more"
                onClick={() =>
                  setExpanded((prev) => {
                    const next = new Set(prev);
                    if (open) next.delete(g.tripId);
                    else next.add(g.tripId);
                    return next;
                  })
                }
              >
                {open ? "Show fewer" : `Show ${g.hits.length - PER_TRIP} more`}
              </button>
            )}
          </section>
        );
      })}
    </div>
  );
}
